import React from 'react';
import { pubSub } from '@tymely/app/AuthContext';
import { Subscription } from 'rxjs';
import { datadogLogs } from '@datadog/browser-logs';
import { AsyncLock } from '@tymely/utils/asyncLock';

type StringOrSequentiable = string | ({ seq?: number } | Record<string, unknown>);
type UnsubscribeCallback = Subscription | undefined;

export const createSubscription = <T extends StringOrSequentiable = StringOrSequentiable>(
    topic: string,
    callback: (value: T, channel: string) => void,
    complete?: () => void,
    gapTimeoutMs: number | null = 5000,
    correlationId: string | null = null,
): UnsubscribeCallback => {
    if (!topic || topic.includes('undefined')) {
        return;
    }

    let expectedSeq = 0;
    const buffer = new Map<number, T>();
    const processedSeqs = new Set<number>();
    const MAX_PROCESSED_SEQS = 1000;

    let gapTimer: ReturnType<typeof setTimeout> | null = null;
    const lock = new AsyncLock();

    const cleanupOldProcessedSeqs = () => {
        if (processedSeqs.size > MAX_PROCESSED_SEQS) {
            const minSeqToKeep = expectedSeq - MAX_PROCESSED_SEQS;
            const seqsToDelete: number[] = [];
            processedSeqs.forEach((seq) => {
                if (seq < minSeqToKeep) {
                    seqsToDelete.push(seq);
                }
            });
            seqsToDelete.forEach((seq) => processedSeqs.delete(seq));
        }
    };

    const clearGapTimer = () => {
        if (gapTimer) {
            clearTimeout(gapTimer);
            gapTimer = null;
        }
    };

    const hasGapNow = () => buffer.size > 0 && !buffer.has(expectedSeq);

    const startGapTimerIfNeeded = () => {
        if (gapTimer !== null) {
            return;
        }

        if (!hasGapNow() || gapTimeoutMs === null) {
            return;
        }

        gapTimer = setTimeout(() => {
            void lock.withLock(() => {
                gapTimer = null;

                if (!hasGapNow()) {
                    return;
                }

                const bufferedSeqs = Array.from(buffer.keys()).sort((a, b) => a - b);
                const nextBufferedSeq = bufferedSeqs[0];

                if (nextBufferedSeq === undefined) {
                    return;
                }

                while (expectedSeq < nextBufferedSeq) {
                    datadogLogs.logger.info(`Event skipped`, {
                        topic,
                        seq: expectedSeq,
                        correlation_id: correlationId,
                    });
                    processedSeqs.add(expectedSeq);
                    expectedSeq = expectedSeq + 1;
                }

                cleanupOldProcessedSeqs();
                flushInOrder();
            });
        }, gapTimeoutMs);
    };

    const flushInOrder = () => {
        let next = buffer.get(expectedSeq);
        while (next !== undefined) {
            buffer.delete(expectedSeq);
            processedSeqs.add(expectedSeq);
            datadogLogs.logger.info(`Pulling buffered event`, {
                topic,
                seq: expectedSeq,
                expected_seq: expectedSeq,
                correlation_id: correlationId,
            });

            callback(next, topic);
            expectedSeq += 1;

            next = buffer.get(expectedSeq);
        }

        cleanupOldProcessedSeqs();

        if (!hasGapNow()) {
            clearGapTimer();
        } else {
            startGapTimerIfNeeded();
        }
    };

    const handleMessage = (event: T) => {
        if (typeof event === 'string' || event.seq === null || event.seq === undefined) {
            return callback(event, topic);
        }

        void lock.withLock(() => {
            const seq = event.seq as number;

            if (processedSeqs.has(seq)) {
                datadogLogs.logger.info(`Duplicate event ignored`, {
                    topic,
                    seq,
                    expected_seq: expectedSeq,
                    correlation_id: correlationId,
                });
                return;
            }

            if (buffer.has(seq)) {
                datadogLogs.logger.info(`Duplicate event ignored (already buffered)`, {
                    topic,
                    seq,
                    expected_seq: expectedSeq,
                    correlation_id: correlationId,
                });
                return;
            }

            if (seq < expectedSeq) {
                datadogLogs.logger.info(`Skipped event arrived`, {
                    topic,
                    seq,
                    expected_seq: expectedSeq,
                    correlation_id: correlationId,
                });
                return;
            }

            if (seq === expectedSeq) {
                processedSeqs.add(seq);
                datadogLogs.logger.info(`Processing event`, {
                    topic,
                    seq,
                    expected_seq: expectedSeq,
                    correlation_id: correlationId,
                });
                expectedSeq += 1;
                callback(event, topic);

                cleanupOldProcessedSeqs();

                flushInOrder();
                return;
            }

            datadogLogs.logger.info(`Buffering event`, { topic, seq, correlation_id: correlationId });
            buffer.set(seq, event);
            startGapTimerIfNeeded();
        });
    };

    const subscription = pubSub.subscribe({ topics: topic }).subscribe({
        next: (data) => handleMessage(data as T),
        complete: () => {
            clearGapTimer();
            complete?.();
        },
    });

    const originalUnsubscribe = subscription.unsubscribe.bind(subscription);
    subscription.unsubscribe = () => {
        clearGapTimer();
        buffer.clear();
        processedSeqs.clear();
        originalUnsubscribe();
    };

    return subscription;
};

export const useSubscription = <T extends StringOrSequentiable = StringOrSequentiable>(
    topic: string,
    callback: (value: T) => void,
    complete?: () => void,
    deps: React.DependencyList = [],
    enabled?: boolean,
) => {
    const subscriptionRef = React.useRef<UnsubscribeCallback>(undefined);

    React.useEffect(() => {
        if (enabled === false) {
            return;
        }

        subscriptionRef.current = createSubscription(topic, callback, complete);

        return () => {
            subscriptionRef.current?.unsubscribe();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [topic, ...deps]);

    React.useEffect(() => () => subscriptionRef.current?.unsubscribe(), []);
};

export const useMultiSubscriptions = <T extends StringOrSequentiable = StringOrSequentiable>(
    topics: string[],
    callback: (value: T, channel: string) => void,
    deps: React.DependencyList = [],
) => {
    const channelsRef = React.useRef(new Map<string, UnsubscribeCallback>());

    React.useEffect(() => {
        topics.forEach((topic) => {
            const subscription = channelsRef.current.get(topic);
            if (!subscription || subscription.closed) {
                channelsRef.current.set(topic, createSubscription(topic, callback));
            }
        });

        channelsRef.current.forEach((subscription, channel) => {
            if (!topics.includes(channel)) {
                subscription?.unsubscribe();
                channelsRef.current.delete(channel);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [topics, ...deps]);

    React.useEffect(
        () => () => {
            channelsRef.current.forEach((subscription) => subscription?.unsubscribe());
            channelsRef.current.clear();
        },
        [],
    );
};

export interface IEvent<T> {
    object_type: string;
    object_id: number | null;
    action: string | null;
    data: T;
    seq?: number;
}

export type UseEventSubscriptionArgs<E> = {
    topic: string;
    enabled?: boolean;
    filter?: (event: E) => boolean;
};

export const useEventSubscription = <T, E extends StringOrSequentiable = IEvent<T>>(
    { topic, enabled, filter }: UseEventSubscriptionArgs<E>,
    callback: (value: E) => void,
    complete?: () => void,
    deps: React.DependencyList = [],
) =>
    useSubscription<E>(
        topic,
        (value) => {
            if (filter === undefined || filter(value)) {
                callback(value);
            }
        },
        complete,
        deps,
        enabled,
    );

type PersistentEvent = Record<string, unknown> & { event: string; interval?: number };

export const usePersistentEvent = <T extends PersistentEvent = PersistentEvent>(
    channel: string | null,
    { interval = 5000, ...data }: T,
    deps: React.DependencyList = [],
    enabled = true,
) => {
    const intervalRef = React.useRef<NodeJS.Timer | undefined>(undefined);

    const stop = () => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }
    };

    React.useEffect(() => {
        if (!enabled) return;
        if (!channel || channel.includes('undefined')) {
            return;
        }
        intervalRef.current = setInterval(() => pubSub.publish({ topics: channel, message: data }), interval);
        return stop;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [channel, interval, ...deps]);

    React.useEffect(() => stop, []);
};
